import { Rental, DbRental, RentalCreate, DbRentalInsert, RentalUpdate, DbRentalUpdate } from '@/types/entities/rental';

// Allowed rental statuses per Supabase enum public.rental_status
const ALLOWED_RENTAL_STATUSES = ['pending', 'active', 'completed', 'cancelled', 'returned', 'overdue'] as const;

// Mapping from Russian UI status names (tag-based) and English to DB status values (case-insensitive)
// Keys must be lowercase
const STATUS_MAPPING: Record<string, string> = {
  // Russian variants (tags)
  'ожидание': 'pending',
  'к сборке': 'pending',
  'активно': 'active',
  'активный': 'active',
  'активная': 'active',
  'завершено': 'completed',
  'завершен': 'completed',
  'завершена': 'completed',
  'отменено': 'cancelled',
  'отменен': 'cancelled',
  'отменена': 'cancelled',
  'возвращено': 'returned',
  'к сдаче': 'returned',
  'просрочено': 'overdue',
  'просрочен': 'overdue',
  'просроченная': 'overdue',
  // English fallbacks
  'pending': 'pending',
  'active': 'active',
  'completed': 'completed',
  'cancelled': 'cancelled',
  'returned': 'returned',
  'overdue': 'overdue',
};

/**
 * Преобразует данные аренды из БД (snake_case) в клиентский формат (camelCase)
 * Согласно STYLEGUIDE.md: Database Layer → Frontend Layer
 */
export function dbRentalToRental(dbRental: DbRental): Rental {
  return {
    id: dbRental.id,
    userId: dbRental.user_id,
    publicId: dbRental.public_id ?? undefined,
    clientId: dbRental.client_id,
    orderId: dbRental.order_id ?? null,
    inventoryItemId: dbRental.inventory_id,
    startDate: dbRental.start_date ? new Date(dbRental.start_date) : null,
    endDate: dbRental.end_date ? new Date(dbRental.end_date) : null,
    status: dbRental.status,
    totalAmount: dbRental.total_amount,
    notes: dbRental.notes,
    createdAt: new Date(dbRental.created_at),
    updatedAt: new Date(dbRental.updated_at),
    // FIXED: Map ALL fields that actually exist in the real schema
    rentalPointId: dbRental.rental_point_id ?? null,
    returnPointId: dbRental.return_point_id ?? null,
    tariffId: dbRental.tariff_id ?? null,
    paymentMethodId: dbRental.payment_method_id ?? null,
    paymentStatus: dbRental.payment_status ?? 'pending',
    inventoryItems: null, // This field doesn't exist in DB schema
    storeItems: null, // This field doesn't exist in DB schema
    discount: dbRental.discount ?? null,
    depositAmount: dbRental.deposit_amount ?? null,
    signatureId: null, // This field doesn't exist in DB schema
    isStorePurchase: false, // This field doesn't exist in DB schema
    // Archive fields
    archivedAt: dbRental.archived_at ? new Date(dbRental.archived_at) : null,
    archivedBy: dbRental.archived_by ?? null,
  };
}

/**
 * Преобразует данные аренды из клиентского формата (camelCase) в формат БД (snake_case)
 * Согласно STYLEGUIDE.md: Frontend Layer → Database Layer
 */
export function rentalToDbRental(rental: Rental): DbRental {
  return {
    id: rental.id,
    user_id: rental.userId,
    public_id: rental.publicId ?? undefined,
    client_id: rental.clientId,
    order_id: rental.orderId ?? null,
    inventory_id: rental.inventoryItemId,
    start_date: rental.startDate ? rental.startDate.toISOString() : null,
    end_date: rental.endDate ? rental.endDate.toISOString() : null,
    status: rental.status,
    total_amount: rental.totalAmount,
    notes: rental.notes,
    created_at: rental.createdAt.toISOString(),
    updated_at: rental.updatedAt.toISOString(),
    // Extended fields for compatibility
    rental_point_id: rental.rentalPointId,
    return_point_id: rental.returnPointId,
    tariff_id: rental.tariffId,
    // Payment fields - both legacy and new columns
    payment_method_id: rental.paymentMethodId,
    payment_method: rental.paymentMethodId,
    payment_status: rental.paymentStatus,
    discount: rental.discount,
    deposit_amount: rental.depositAmount,
    // Read optional promoCode defensively from unknown shape
    promo_code: (() => {
      const extra = rental as unknown as Record<string, unknown>;
      const value = extra && typeof extra.promoCode === 'string' ? extra.promoCode : null;
      return value;
    })(),
    signature_id: rental.signatureId,
    is_store_purchase: rental.isStorePurchase,
    // Archive fields
    archived_at: rental.archivedAt ? rental.archivedAt.toISOString() : null,
    archived_by: rental.archivedBy ?? null,
  };
}

/**
 * Преобразует данные создания аренды из клиентского формата в формат БД
 * Согласно STYLEGUIDE.md: Frontend Layer → API Layer
 */
export function rentalCreateToDbInsert(rentalCreate: RentalCreate): DbRentalInsert {
  // Normalize status: trim + lowercase, then map and validate before inserting
  const rawStatus = (rentalCreate.status as unknown as string) ?? '';
  const normalized = typeof rawStatus === 'string' ? rawStatus.trim().toLowerCase() : '';
  const mappedStatus = (STATUS_MAPPING as Record<string, string>)[normalized] || normalized;
  
  // Use 'active' as default if status is invalid or missing
  let validStatus = 'active';
  if ((ALLOWED_RENTAL_STATUSES as readonly string[]).includes(mappedStatus)) {
    validStatus = mappedStatus;
    // eslint-disable-next-line no-console
    console.log(`[rentalMappers] Mapped status "${rawStatus}" → "${mappedStatus}"`);
  } else {
    // eslint-disable-next-line no-console
    console.warn(`[rentalMappers] Invalid rental status "${rawStatus}", using default "active"`);
  }

  // FIXED: Map ALL fields that actually exist in the rentals table schema
  // Based on the real DB schema provided by user
  const base: Record<string, unknown> = {
    user_id: rentalCreate.userId,
    client_id: rentalCreate.clientId,
    order_id: (() => {
      const rc = rentalCreate as unknown as Record<string, unknown>;
      return rc && typeof rc.orderId !== 'undefined' ? (rc.orderId as string | null) : null;
    })(),
    inventory_id: rentalCreate.inventoryItemId,
    start_date: rentalCreate.startDate ? rentalCreate.startDate.toISOString() : null,
    end_date: rentalCreate.endDate ? rentalCreate.endDate.toISOString() : null,
    status: validStatus,
    total_amount: rentalCreate.totalAmount,
    notes: rentalCreate.notes,
  };

  // Map additional fields that actually exist in the schema
  const extendedCreate = rentalCreate as unknown as Record<string, unknown>;
  
  // Map depositAmount to deposit_amount (this field exists in schema)
  if ('depositAmount' in extendedCreate && typeof extendedCreate.depositAmount === 'number') {
    base.deposit_amount = extendedCreate.depositAmount;
  }

  // FIXED: Map all extended fields that exist in real schema
  if ('rentalPointId' in extendedCreate && extendedCreate.rentalPointId) {
    base.rental_point_id = extendedCreate.rentalPointId;
  }

  if ('returnPointId' in extendedCreate && extendedCreate.returnPointId) {
    base.return_point_id = extendedCreate.returnPointId;
  }

  if ('tariffId' in extendedCreate && extendedCreate.tariffId) {
    base.tariff_id = extendedCreate.tariffId;
  }

  if ('paymentMethodId' in extendedCreate && extendedCreate.paymentMethodId) {
    base.payment_method_id = extendedCreate.paymentMethodId;
  }

  if ('paymentStatus' in extendedCreate && extendedCreate.paymentStatus) {
    // If value looks like a UUID (id of a tag), skip setting it to let DB default ('pending') apply
    const ps = String(extendedCreate.paymentStatus);
    const UUID_V4_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (!UUID_V4_REGEX.test(ps)) {
      base.payment_status = ps;
    } else {
      console.warn('[rentalCreateToDbInsert] payment_status is UUID-like, skipping to use DB default');
    }
  }

  if ('discount' in extendedCreate && typeof extendedCreate.discount === 'number') {
    base.discount = extendedCreate.discount;
  }

  if ('promoCode' in extendedCreate && extendedCreate.promoCode) {
    base.promo_code = extendedCreate.promoCode;
  }

  // eslint-disable-next-line no-console
  console.log('[rentalCreateToDbInsert] Mapped rental data for DB insert:', base);
  // eslint-disable-next-line no-console
  console.log('[rentalCreateToDbInsert] Successfully mapped all extended fields: rental_point_id, return_point_id, tariff_id, payment_method_id, payment_status, discount, promo_code');

  return { ...base } as DbRentalInsert;
}

/**
 * Преобразует данные обновления аренды из клиентского формата в формат БД
 * Согласно STYLEGUIDE.md: Frontend Layer → API Layer
 */
export function rentalUpdateToDbUpdate(rentalUpdate: Partial<Rental>): Partial<DbRentalUpdate> {
  // eslint-disable-next-line no-console
  console.log('[rentalUpdateToDbUpdate] Input data:', rentalUpdate);
  const dbUpdate: Partial<DbRentalUpdate> = {};

  if (rentalUpdate.userId !== undefined) dbUpdate.user_id = rentalUpdate.userId;
  if (rentalUpdate.clientId !== undefined) dbUpdate.client_id = rentalUpdate.clientId;
  if (rentalUpdate.orderId !== undefined) dbUpdate.order_id = rentalUpdate.orderId ?? null;
  if (rentalUpdate.inventoryItemId !== undefined) dbUpdate.inventory_id = rentalUpdate.inventoryItemId;
  if (rentalUpdate.startDate !== undefined) {
    if (rentalUpdate.startDate === null) {
      dbUpdate.start_date = null;
    } else if (rentalUpdate.startDate instanceof Date) {
      dbUpdate.start_date = rentalUpdate.startDate.toISOString();
    } else if (typeof rentalUpdate.startDate === 'string') {
      // Если это строка, преобразуем в Date и затем в ISO string
      dbUpdate.start_date = new Date(rentalUpdate.startDate).toISOString();
    } else {
      dbUpdate.start_date = null;
    }
  }
  if (rentalUpdate.endDate !== undefined) {
    if (rentalUpdate.endDate === null) {
      dbUpdate.end_date = null;
    } else if (rentalUpdate.endDate instanceof Date) {
      dbUpdate.end_date = rentalUpdate.endDate.toISOString();
    } else if (typeof rentalUpdate.endDate === 'string') {
      // Если это строка, преобразуем в Date и затем в ISO string
      dbUpdate.end_date = new Date(rentalUpdate.endDate).toISOString();
    } else {
      dbUpdate.end_date = null;
    }
  }
  if (rentalUpdate.status !== undefined) {
    // Map Russian UI status names to DB values
    const statusValue = rentalUpdate.status as unknown as string;
    const mappedStatus = STATUS_MAPPING[statusValue] || statusValue;
    
    // Defensive: only pass through statuses allowed by DB enum/check constraint
    if ((ALLOWED_RENTAL_STATUSES as readonly string[]).includes(mappedStatus)) {
      dbUpdate.status = mappedStatus as unknown as DbRentalUpdate['status'];
      // eslint-disable-next-line no-console
      console.log(`[rentalMappers] Mapped status "${statusValue}" → "${mappedStatus}"`);
    } else {
      // Do not propagate invalid status to DB to avoid rentals_status_check violation
      if (typeof console !== 'undefined') {
        // eslint-disable-next-line no-console
        console.warn('[rentalMappers] Ignoring invalid rental status in update:', statusValue, 'mapped to:', mappedStatus);
      }
    }
  }
  if (rentalUpdate.totalAmount !== undefined) dbUpdate.total_amount = rentalUpdate.totalAmount;
  if (rentalUpdate.notes !== undefined) dbUpdate.notes = rentalUpdate.notes;

  // FIXED: Map ALL fields that actually exist in the rentals table schema
  // Extended fields mapping for updates (camelCase → snake_case)
  const extendedUpdate = rentalUpdate as unknown as Record<string, unknown>;
  
  // Map depositAmount to deposit_amount (this field exists in schema)
  if ('depositAmount' in extendedUpdate && typeof extendedUpdate.depositAmount === 'number') {
    dbUpdate.deposit_amount = extendedUpdate.depositAmount as number;
  }

  // Map all extended fields that exist in real schema
  if ('rentalPointId' in extendedUpdate) {
    const value = extendedUpdate.rentalPointId as string | null | undefined;
    if (value && value !== '' && value !== null && value !== undefined) {
      dbUpdate.rental_point_id = value as string;
    }
  }

  if ('returnPointId' in extendedUpdate) {
    const value = extendedUpdate.returnPointId as string | null | undefined;
    if (value && value !== '' && value !== null && value !== undefined) {
      dbUpdate.return_point_id = value as string;
    }
  }

  if ('tariffId' in extendedUpdate) {
    const value = extendedUpdate.tariffId as string | null | undefined;
    if (value && value !== '' && value !== null && value !== undefined) {
      dbUpdate.tariff_id = value as string;
    }
  }

  if ('paymentMethodId' in extendedUpdate) {
    const value = extendedUpdate.paymentMethodId as string | null | undefined;
    if (value && value !== '' && value !== null && value !== undefined) {
      dbUpdate.payment_method_id = value as string;
    }
  }

  if ('paymentStatus' in extendedUpdate) {
    const value = extendedUpdate.paymentStatus as string | null | undefined;
    if (value && value !== '' && value !== null && value !== undefined) {
      dbUpdate.payment_status = value as string;
    }
  }

  if ('discount' in extendedUpdate && typeof extendedUpdate.discount === 'number') {
    dbUpdate.discount = extendedUpdate.discount as number;
  }

  if ('promoCode' in extendedUpdate) {
    const value = (extendedUpdate as Record<string, unknown>).promoCode as string | null | undefined;
    if (value && value !== '' && value !== null && value !== undefined) {
      dbUpdate.promo_code = value as string;
    }
  }

  // Archive fields mapping
  if ('archivedAt' in extendedUpdate) {
    const value = extendedUpdate.archivedAt as Date | null | undefined;
    dbUpdate.archived_at = value ? new Date(value as Date).toISOString() : null;
  }
  if ('archivedBy' in extendedUpdate) {
    const value = extendedUpdate.archivedBy as string | null | undefined;
    dbUpdate.archived_by = value ?? null;
  }

  // eslint-disable-next-line no-console
  console.log('[rentalUpdateToDbUpdate] Successfully mapped all extended fields: rental_point_id, return_point_id, tariff_id, payment_method_id, payment_status, discount, promo_code');

  // eslint-disable-next-line no-console
  console.log('[rentalUpdateToDbUpdate] Output data:', dbUpdate);
  return dbUpdate;
}
